import {useMemo} from 'react'
import {useIssueComments} from '../../../../stores/layout-selectors'
import {useReviews} from './use-reviews'
import type {IssueComment} from '../../../../page-data/payloads/issue-comments'
import type {ReviewWithThreads} from '../../../comments/utils/assign-threads-to-reviews'
import type {Thread} from '@github-ui/diff-lines/conversation-types'

type CommentOrReview =
  | {type: 'comment'; data: IssueComment}
  | {type: 'review'; data: ReviewWithThreads}
  | {type: 'thread'; data: Thread}

function getCreatedAt(item: CommentOrReview) {
  switch (item.type) {
    case 'comment':
      return new Date(item.data.createdAt)
    case 'review':
      return new Date(item.data.summary.submittedAt)
    case 'thread':
      return new Date(item.data.commentsData?.comments?.[0]?.createdAt ?? 0)
  }
}

/** Top-level comments (issue comments), unassigned threads, and reviews, mixed together in chrono (oldest -> newest) order. */
export function useCommentsAndReviews() {
  const issueComments = useIssueComments()
  const {reviews, unassignedThreads} = useReviews()

  return useMemo<CommentOrReview[]>(
    () =>
      [
        ...issueComments.map<CommentOrReview>(issue => ({type: 'comment', data: issue})),
        ...reviews.map<CommentOrReview>(review => ({type: 'review', data: review})),
        ...unassignedThreads.map<CommentOrReview>(thread => ({type: 'thread', data: thread})),
      ].sort((a, b) => (getCreatedAt(a) < getCreatedAt(b) ? -1 : 1)),
    [issueComments, reviews, unassignedThreads],
  )
}
